<script setup>
import DateRangePicker from '@js/components/pickers/DateRangePicker.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatNumber, formatPrice } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, googleAdsProductManagement } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    googleAdsProductManagement: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.cms_product_id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID' + '<br/>' + 'Produit',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsProductId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.cms_product_variation_id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID' + '<br/>' + 'Variation de produit',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsProductVariationId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sku',
            header: () => 'SKU',
            cell: (props) =>
                props.row.original.sku
                    ? h(TextCell, {
                          text: props.row.original.sku,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_product_languages.name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: props.row.original.url,
                    target: '_blank',
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'finalPriceExclTax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix<br/>Produit HT',
                }),
            cell: (props) =>
                props.row.original.finalPriceExclTax
                    ? h(PriceCell, {
                          price: props.row.original.finalPriceExclTax,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'finalPriceInclTax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix<br/>Produit TTC',
                }),
            cell: (props) =>
                props.row.original.finalPriceInclTax
                    ? h(PriceCell, {
                          price: props.row.original.finalPriceInclTax,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'calculatedProductCost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût<br/>Produit HT',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.calculatedProductCost,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'maximumExpenses',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dépenses' + '<br/>' + 'Max',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.maximumExpenses,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'roasTarget',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS' + '<br/>' + 'Cible',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.roasTarget,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'manualCpc',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CPC' + '<br/>' + 'Manuel',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.manualCpc,
                }),
        }),
        // columnHelper.accessor(() => null, {
        //     id: 'campaign',
        //     header: () => 'Campagne',
        //     cell: (props) =>
        //         h(PillCell, {
        //             pillValue: props.row.original.campaign,
        //             type: props.row.original.campaign.includes('R1')
        //                 ? 'success'
        //                 : props.row.original.campaign.includes('R2')
        //                   ? 'warning'
        //                   : props.row.original.campaign.includes('R3') ||
        //                       props.row.original.campaign.includes('R-Probleme')
        //                     ? 'danger'
        //                     : 'secondary',
        //         }),
        //     enableSorting: false,
        // }),
        columnHelper.accessor(() => null, {
            id: 'google_ads_product_management_items.campaign_group',
            header: () => 'Campagne',
            cell: (props) =>
                props.row.original.campaignGroup
                    ? h(PillCell, {
                          pillValue: props.row.original.campaignGroup,
                          type: props.row.original.campaign.includes('R1')
                              ? 'success'
                              : props.row.original.campaign.includes('R2')
                                ? 'warning'
                                : props.row.original.campaign.includes('R3') ||
                                    props.row.original.campaign.includes(
                                        'R-Probleme',
                                    )
                                  ? 'danger'
                                  : 'secondary',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'google_ads_product_management_items.zombie_revival',
            header: () => 'Zombie',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.zombieRevival ? 'Oui' : 'Non',
                    type: props.row.original.zombieRevival
                        ? 'warning'
                        : 'secondary',
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'quantity',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Stock',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.quantity,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'quantitySold',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nb<br/>ventes',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.quantitySold,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'period',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Période',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [h('span', '7J')]),
                            h('div', {}, [h('span', '30J')]),
                            h('div', {}, [h('span', '90J')]),
                            h('div', {}, [h('span', '365J')]),
                            h('div', {}, [h('span', '3 ANS')]),
                        ],
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'googleAdsCosts',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût' + '<br/>' + 'Ads',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.googleAdsCosts[
                                            'over_7_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.googleAdsCosts[
                                            'over_30_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.googleAdsCosts[
                                            'over_90_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.googleAdsCosts[
                                            'over_365_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatPrice(
                                    props.row.original.googleAdsCosts['total'],
                                ),
                            ]),
                        ],
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'googleAdsRoas',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS' + '<br/>' + 'Ads',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.googleAdsRoas[
                                            'over_7_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.googleAdsRoas[
                                            'over_30_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.googleAdsRoas[
                                            'over_90_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.googleAdsRoas[
                                            'over_365_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.googleAdsRoas[
                                            'total'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                        ],
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'websiteRoas',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS' + '<br/>' + 'Site',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.websiteRoas[
                                            'over_7_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.websiteRoas[
                                            'over_30_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.websiteRoas[
                                            'over_90_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.websiteRoas[
                                            'over_365_days'
                                        ],
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.websiteRoas['total'],
                                        false,
                                    ),
                                ),
                            ]),
                        ],
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'averageGoogleAdsCpc',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CPC' + '<br/>' + 'Moyen',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.averageGoogleAdsCpc[
                                            'over_7_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.averageGoogleAdsCpc[
                                            'over_30_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.averageGoogleAdsCpc[
                                            'over_90_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.averageGoogleAdsCpc[
                                            'over_365_days'
                                        ],
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatPrice(
                                    props.row.original.averageGoogleAdsCpc[
                                        'total'
                                    ],
                                ),
                            ]),
                        ],
                    },
                ),
            enableSorting: false,
        }),
    ],
    url: route('front.projectManagement.sea.postProductManagementItems', {
        tenant: tenant.id,
        googleAdsProductManagement: googleAdsProductManagement.id,
    }),
    columnVisibility: {
        'e_commerce_products.cms_product_variation_id':
            tenant.cms === 'prestashop' || tenant.cms === 'shopify',
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        force-scroll
    >
        <template #actions>
            <DateRangePicker :callback="updateData" />
        </template>
    </DataTableLayout>
</template>
